import { sectionNames } from '@common/intl'
import type { SidebarMenuItem } from '@components/Sidebar'
import { Permission, UserRole } from '@gql'
import useUser from '@hooks/useUser'
import MenuCaseIcon from '@icons/MenuCaseIcon'
import MenuConfigureIcon from '@icons/MenuConfigureIcon'
import MenuCustomerIcon from '@icons/MenuCustomerIcon'
import MenuFinancialProvidersIcon from '@icons/MenuFinancialProvidersIcon'
import MenuHomeIcon from '@icons/MenuHomeIcon'
import MenuMessageIcon from '@icons/MenuMessageIcon'
import BookOnlineIcon from '@mui/icons-material/BookOnlineOutlined'
import InboxIcon from '@mui/icons-material/InboxOutlined'
import MenuReportIcon from '@mui/icons-material/SummarizeOutlined'
import { appSection } from '@sections/apps/urls'
import { hasUserAccess } from '@sections/auth/access'
import useReminderBadgeCount from '@sections/cases/hooks/useReminderBadgeCount'
import {
  caseListAllUrl,
  caseListTeamAssignedUrl,
  caseListUserAssignedUrl,
  caseRemindersUrl,
  caseSection,
} from '@sections/cases/urls'
import { channelSection } from '@sections/channels/urls'
import { configurationMenuUrl } from '@sections/configuration'
import { getConfigMenuItemsPermissions } from '@sections/configuration/utils'
import { customerListUrl } from '@sections/customers/urls'
import { financialProviderListUrl } from '@sections/financialProviders/urls'
import useLeadQueueBacklog from '@sections/leadQueues/hooks/useLeadQueueBacklog'
import { leadQueueSection, LeadQueueTab, leadQueueUrl } from '@sections/leadQueues/urls'
import { NavUnreadBadge } from '@sections/messaging/components/common/NavUnreadBadge'
import { useUnreadConversationCount } from '@sections/messaging/hooks/useUnread'
import { messagingHomeUrl, messagingSection } from '@sections/messaging/urls'
import { oversightScenarioSection } from '@sections/oversightScenarios/urls'
import { playbookSection } from '@sections/playbooks/urls'
import {
  reportAcquisitionFunnelUrl,
  reportAssignedLeadsUrl,
  reportFeesUrl,
  reportLeadDuplicatesUrl,
  reportMessagingUrl,
  reportNewLeadsUrl,
  reportSection,
  reportUserActivityUrl,
} from '@sections/reports/urls'
import { settingsSection } from '@sections/settings/urls'
import type { TicketDetailType } from '@sections/tickets/urls'
import {
  ticketDetailPath,
  ticketListAllUrl,
  ticketListTeamAssignedUrl,
  ticketListUserAssignedUrl,
  ticketSection,
} from '@sections/tickets/urls'
import { userSection } from '@sections/users/urls'
import { pick } from 'lodash-es'
import { useIntl } from 'react-intl'

type AccessType = { permission: Permission } | { role: UserRole }
type Access = AccessType | { any: AccessType[] } | { all: AccessType[] }

export interface FilterableMenuItem extends Omit<SidebarMenuItem, 'children'> {
  children?: FilterableMenuItem[]
  access?: Access
}

function useMenuStructure(): SidebarMenuItem[] {
  const { user } = useUser()
  const intl = useIntl()
  const unreadCount = useUnreadConversationCount()
  const leadQueueBacklog = useLeadQueueBacklog()
  const overdueReminders = useReminderBadgeCount()
  const reminderBadge =
    overdueReminders > 0 ? <NavUnreadBadge count={overdueReminders} color="error" /> : undefined

  const menuItems: FilterableMenuItem[] = [
    {
      ariaLabel: 'home',
      icon: <MenuHomeIcon />,
      id: 'home',
      label: intl.formatMessage(sectionNames.home),
      url: '/',
    },
    {
      access: {
        any: [
          { role: UserRole.OPERATOR },
          { role: UserRole.TEAMLEADER },
          { permission: Permission.MANAGE_CASES },
        ],
      },
      activeMatchUrls: [caseSection],
      ariaLabel: 'cases',
      // Shown on the parent too, so a collapsed Cases group still says so.
      badge: reminderBadge,
      children: [
        {
          ariaLabel: 'casesAssigned',
          id: 'casesAssigned',
          label: intl.formatMessage(sectionNames.casesUserAssigned),
          url: caseListUserAssignedUrl(),
        },
        {
          access: { role: UserRole.TEAMLEADER },
          ariaLabel: 'casesTeamAssigned',
          id: 'casesTeamAssigned',
          label: intl.formatMessage(sectionNames.casesTeamAssigned),
          url: caseListTeamAssignedUrl(),
        },
        {
          access: { permission: Permission.MANAGE_CASES },
          ariaLabel: 'casesAll',
          id: 'casesAll',
          label: intl.formatMessage(sectionNames.casesAll),
          url: caseListAllUrl(),
        },
        {
          ariaLabel: 'casesReminders',
          badge: reminderBadge,
          id: 'casesReminders',
          label: intl.formatMessage(sectionNames.casesReminders),
          url: caseRemindersUrl(),
        },
      ],
      icon: <MenuCaseIcon />,
      id: 'cases',
      label: intl.formatMessage(sectionNames.cases),
    },
    {
      access: { permission: Permission.USE_MESSAGING },
      activeMatchUrls: [messagingSection],
      ariaLabel: 'messages',
      badge: unreadCount > 0 ? <NavUnreadBadge count={unreadCount} /> : undefined,
      icon: <MenuMessageIcon />,
      id: 'messages',
      label: intl.formatMessage(sectionNames.messages),
      url: messagingHomeUrl(),
    },
    {
      access: {
        any: [
          { role: UserRole.MEDITATOR },
          { role: UserRole.NEGOTIATOR },
          { role: UserRole.TEAMLEADER },
          { permission: Permission.MANAGE_CASES },
        ],
      },
      activeMatchUrls: [ticketSection],
      ariaLabel: 'tickets',
      children: [
        {
          activeMatchUrls: [ticketDetailPath(':type' as TicketDetailType, 'assigned', '*')],
          ariaLabel: 'ticketsAssigned',
          id: 'ticketsAssigned',
          label: intl.formatMessage(sectionNames.ticketsUserAssigned),
          url: ticketListUserAssignedUrl(),
        },
        {
          access: { role: UserRole.TEAMLEADER },
          activeMatchUrls: [ticketDetailPath(':type' as TicketDetailType, 'team-assigned', '*')],
          ariaLabel: 'ticketsTeamAssigned',
          id: 'ticketsTeamAssigned',
          label: intl.formatMessage(sectionNames.ticketsTeamAssigned),
          url: ticketListTeamAssignedUrl(),
        },
        {
          access: { permission: Permission.MANAGE_CASES },
          activeMatchUrls: [ticketDetailPath(':type' as TicketDetailType, 'all', '*')],
          ariaLabel: 'ticketsAll',
          id: 'ticketsAll',
          label: intl.formatMessage(sectionNames.ticketsAll),
          url: ticketListAllUrl(),
        },
      ],
      icon: <BookOnlineIcon />,
      id: 'tickets',
      label: intl.formatMessage(sectionNames.tickets),
    },
    {
      activeMatchUrls: [reportSection],
      ariaLabel: 'report',
      children: [
        {
          access: { permission: Permission.MANAGE_CASES },
          ariaLabel: 'newLeads',
          id: 'newLeads',
          label: intl.formatMessage(sectionNames.reportNewLeads),
          url: reportNewLeadsUrl(),
        },
        {
          access: {
            any: [{ permission: Permission.MANAGE_CASES }, { role: UserRole.TEAMLEADER }],
          },
          ariaLabel: 'assignedLeads',
          id: 'assignedLeads',
          label: intl.formatMessage(sectionNames.reportAssignedLeads),
          url: reportAssignedLeadsUrl(),
        },
        {
          access: {
            any: [{ permission: Permission.MANAGE_CASES }, { role: UserRole.TEAMLEADER }],
          },
          ariaLabel: 'userActivity',
          id: 'userActivity',
          label: intl.formatMessage(sectionNames.reportUserActivity),
          url: reportUserActivityUrl(),
        },
        {
          access: { permission: Permission.READ_FINANCE },
          ariaLabel: 'fees',
          id: 'fees',
          label: intl.formatMessage(sectionNames.reportFees),
          url: reportFeesUrl(),
        },
        {
          access: { permission: Permission.READ_ACQUISITION },
          ariaLabel: 'acquisitionFunnel',
          id: 'acquisitionFunnel',
          label: intl.formatMessage(sectionNames.reportAcquisitionFunnel),
          url: reportAcquisitionFunnelUrl(),
        },
        {
          access: { permission: Permission.READ_ACQUISITION },
          ariaLabel: 'reportLeadDuplicates',
          id: 'reportLeadDuplicates',
          label: intl.formatMessage(sectionNames.reportLeadDuplicates),
          url: reportLeadDuplicatesUrl(),
        },
        {
          access: { permission: Permission.READ_MESSAGING },
          ariaLabel: 'reportMessaging',
          id: 'reportMessaging',
          label: intl.formatMessage(sectionNames.reportMessaging),
          url: reportMessagingUrl(),
        },
      ],
      icon: <MenuReportIcon />,
      id: 'reports',
      label: intl.formatMessage(sectionNames.reports),
    },
    {
      ariaLabel: 'customers',
      icon: <MenuCustomerIcon />,
      id: 'customers',
      label: intl.formatMessage(sectionNames.customers),
      url: customerListUrl(),
    },
    {
      // MANAGE_CASES is what the API requires for the quarantine's reads AND
      // actions, so this never offers a nav entry the server would reject.
      access: { permission: Permission.MANAGE_CASES },
      activeMatchUrls: [leadQueueSection],
      ariaLabel: 'leadQueues',
      // One badge over the queues this operator can see: a count they cannot
      // act on would send them to a screen with nothing on it.
      badge:
        leadQueueBacklog.total > 0 ? <NavUnreadBadge count={leadQueueBacklog.total} /> : undefined,
      icon: <InboxIcon />,
      id: 'leadQueues',
      label: intl.formatMessage(sectionNames.leadQueues),
      url: leadQueueUrl(LeadQueueTab.quarantine),
    },
    {
      access: { permission: Permission.MANAGE_FINANCIAL_PROVIDERS },
      ariaLabel: 'financialProviders',
      icon: <MenuFinancialProvidersIcon />,
      id: 'financialProviders',
      label: intl.formatMessage(sectionNames.financialProviders),
      url: financialProviderListUrl(),
    },
    {
      access: {
        any: getConfigMenuItemsPermissions(intl).map((permission) => ({
          permission,
        })),
      },
      activeMatchUrls: [
        settingsSection,
        userSection,
        appSection,
        channelSection,
        playbookSection,
        oversightScenarioSection,
      ],
      ariaLabel: 'configure',
      icon: <MenuConfigureIcon />,
      id: 'configure',
      label: intl.formatMessage(sectionNames.configuration),
      url: configurationMenuUrl,
    },
  ]

  const isMenuItemPermitted = (menuItem: FilterableMenuItem) => {
    if (!menuItem.access) {
      return true
    }

    return hasUserAccess(user, menuItem.access)
  }

  const getFilteredMenuItems = (menuItems: FilterableMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted)

  return menuItems.reduce((resultItems: FilterableMenuItem[], menuItem: FilterableMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems
    }

    const { children } = menuItem
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined

    return [
      ...resultItems,
      ...(filteredChildren?.length !== 0
        ? [
            {
              ...menuItem,
              // If only one filtered record, pass its properties to root
              ...(filteredChildren?.length === 1
                ? {
                    children: undefined,
                    // For now only pick url
                    ...pick(filteredChildren[0], 'url'),
                  }
                : {
                    children: filteredChildren,
                  }),
            },
          ]
        : []),
    ]
  }, [] as FilterableMenuItem[])
}

export default useMenuStructure
